import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { calculate, SYRINGES, DOSE_UNITS, DEFAULT_SYRINGE } from '../lib/peptide.js'
import { toKey, addDays, fromKey, todayKey } from '../lib/dates.js'
import { useAuth } from '../context/AuthContext.jsx'
import { useUserData } from '../lib/useUserData.js'
import { useToast } from '../context/ToastContext.jsx'
import Seo from '../components/Seo.jsx'
import SyringeVisual from '../components/SyringeVisual.jsx'

const FREQS = [
  { id: 7, noun: 'week', label: 'per week' },
  { id: 1, noun: 'day', label: 'per day' },
  { id: 2, noun: 'dose', label: 'every 2 days' },
  { id: 3, noun: 'dose', label: 'every 3 days' },
]

let _pid = 0
const newPhase = (p = {}) => ({ id: ++_pid, dose: '', doseUnit: 'mg', count: '', ...p })

export default function Guide() {
  const { user } = useAuth()
  const { addEvents } = useUserData()
  const toast = useToast()
  const navigate = useNavigate()

  const [f, setF] = useState({ name: '', vialMg: '', waterMl: '', syringe: DEFAULT_SYRINGE, freq: 7, start: todayKey() })
  const [phases, setPhases] = useState([newPhase()])
  const [busy, setBusy] = useState(false)
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }))

  const freq = FREQS.find((x) => x.id === Number(f.freq)) || FREQS[0]
  const cap = SYRINGES.find((s) => s.id === f.syringe)?.capacity ?? 100
  const unitName = (id) => DOSE_UNITS.find((u) => u.id === id)?.label || 'mg'

  // compute each phase off the same reconstituted vial
  const computed = useMemo(
    () => phases.map((p) => ({
      p,
      res: calculate({ vialMg: f.vialMg, waterMl: f.waterMl, dose: p.dose, doseUnit: p.doseUnit, syringe: f.syringe }),
      count: Math.max(0, Math.floor(Number(p.count) || 0)),
    })),
    [phases, f.vialMg, f.waterMl, f.syringe]
  )
  const ready = Number(f.vialMg) > 0 && Number(f.waterMl) > 0 && computed[0]?.res.ok
  const first = computed[0]
  // Surface physical-impossibility warnings (draw exceeds the syringe, too small
  // to measure, etc.) — the math is right, but the result may not be usable.
  const warnings = [...new Set(computed.flatMap((c) => (c.res.ok ? c.res.warnings : [])))]
  const firstOverCap = first?.res.ok && first.res.units > cap

  const addPhase = () => setPhases((x) => [...x, newPhase({ doseUnit: x[x.length - 1]?.doseUnit || 'mg' })])
  const removePhase = (id) => setPhases((x) => (x.length > 1 ? x.filter((p) => p.id !== id) : x))
  const updatePhase = (id, k, v) => setPhases((x) => x.map((p) => (p.id === id ? { ...p, [k]: v } : p)))

  const scheduleAll = async () => {
    const events = []
    let dayCursor = 0
    for (const c of computed) {
      if (!c.res.ok || c.count <= 0) continue
      for (let i = 0; i < c.count; i++) {
        events.push({
          date: toKey(addDays(fromKey(f.start), dayCursor * freq.id)),
          time: '08:00',
          name: f.name.trim() || 'Dose',
          dose: c.p.dose,
          doseUnit: c.p.doseUnit,
          units: c.res.units,
        })
        dayCursor++
      }
    }
    if (events.length === 0) return toast('Add how many doses at each step first', 'red')
    setBusy(true)
    const res = await addEvents(events)
    setBusy(false)
    if (res?.error) return toast(res.error, 'red')
    toast(`${events.length} doses added to your calendar`, 'green')
    navigate('/calendar')
  }

  return (
    <main className="page">
      <Seo
        title="Guided Peptide Mixing Calculator | CalculateMyPeptides"
        description="A step-by-step, plain-English reconstitution calculator. Fill in your vial, prescribed dose, and water — get simple instructions for how many units to draw, plus an optional titration schedule."
      />
      <div className="container page-narrow">
        <span className="pill blue" style={{ marginBottom: 8 }}>Step-by-step</span>
        <h1 className="section-title">Guided mixing calculator</h1>
        <p className="section-sub" style={{ maxWidth: '58ch' }}>
          New to reconstitution? Fill in the blanks below with what you were prescribed and what you have on
          hand — we’ll turn it into simple, plain-English steps.
        </p>

        <div className="card card-pad guide-card">
          <p className="guide-sentence">
            I have a{' '}
            <input className="blank" style={{ width: 62 }} type="number" min="0" step="any" inputMode="decimal"
              placeholder="10" value={f.vialMg} onChange={(e) => set('vialMg', e.target.value)} /> mg vial of{' '}
            <input className="blank" style={{ width: 130 }} placeholder="GLP-1"
              value={f.name} onChange={(e) => set('name', e.target.value)} />.
            {' '}I added{' '}
            <input className="blank" style={{ width: 56 }} type="number" min="0" step="any" inputMode="decimal"
              placeholder="2" value={f.waterMl} onChange={(e) => set('waterMl', e.target.value)} /> mL of
            bacteriostatic water, and I have a{' '}
            <select className="blank" value={f.syringe} onChange={(e) => set('syringe', e.target.value)}>
              {SYRINGES.map((s) => <option key={s.id} value={s.id}>{s.capacity}-unit</option>)}
            </select>{' '}insulin syringe.
          </p>
          <p className="field-help" style={{ marginTop: 4 }}>
            Not sure how much water? 1–2 mL is common — it doesn’t change your dose, only how many units you draw.
          </p>

          <div className="divider" />

          <h3 style={{ fontSize: '1rem', marginBottom: 4 }}>My prescribed dose</h3>
          <p className="field-help" style={{ marginTop: 0, marginBottom: 12 }}>Enter exactly what your prescriber told you. Add a step if your dose changes (titration).</p>

          <div className="field" style={{ maxWidth: 220 }}>
            <label className="label sm">How often?</label>
            <select className="select" value={f.freq} onChange={(e) => set('freq', e.target.value)}>
              {FREQS.map((x) => <option key={x.id} value={x.id}>{x.label}</option>)}
            </select>
          </div>

          {phases.map((p, i) => (
            <div className="guide-phase" key={p.id}>
              <span className="guide-phase-n">{i === 0 ? 'Start' : `Then`}</span>
              <input className="input" type="number" min="0" step="any" placeholder="2.5" style={{ maxWidth: 90 }}
                value={p.dose} onChange={(e) => updatePhase(p.id, 'dose', e.target.value)} />
              <div className="seg">
                {DOSE_UNITS.map((u) => (
                  <button key={u.id} type="button" className={p.doseUnit === u.id ? 'active' : ''}
                    onClick={() => updatePhase(p.id, 'doseUnit', u.id)}>{u.label}</button>
                ))}
              </div>
              <span className="muted small">for</span>
              <input className="input" type="number" min="0" step="1" placeholder="4" style={{ maxWidth: 70 }}
                value={p.count} onChange={(e) => updatePhase(p.id, 'count', e.target.value)} />
              <span className="muted small">{freq.noun}s</span>
              {phases.length > 1 && (
                <button className="extra-remove" onClick={() => removePhase(p.id)} aria-label="Remove">✕</button>
              )}
            </div>
          ))}
          <button className="btn btn-ghost btn-sm add-extra" onClick={addPhase} style={{ marginTop: 10 }}>
            ＋ Add a dose change (titration)
          </button>
        </div>

        {/* ---------- output ---------- */}
        {ready ? (
          <div className="card guide-out fade-in">
            <div className="guide-out-head">Here’s how to mix &amp; dose it</div>
            <div className="guide-out-body">
              <ol className="guide-steps">
                <li>Add <strong>{f.waterMl} mL</strong> of bacteriostatic water to your <strong>{f.vialMg} mg</strong> vial{f.name.trim() ? <> of <strong>{f.name.trim()}</strong></> : ''} and let it dissolve.</li>
                <li>
                  For each dose, draw the number of units shown below into your <strong>{cap}-unit</strong> syringe:
                  <div className="guide-schedule">
                    {computed.map((c, i) => (
                      c.res.ok ? (
                        <div className="guide-sched-row" key={c.p.id}>
                          <span className="gs-dose">{c.p.dose} {unitName(c.p.doseUnit)} {freq.label}{computed.length > 1 && c.count ? ` × ${c.count}` : ''}</span>
                          <span className="gs-units" style={c.res.units > cap ? { color: 'var(--red)' } : undefined}>
                            {c.res.units} units{c.res.units > cap ? ' ⚠️' : ''}
                          </span>
                        </div>
                      ) : null
                    ))}
                  </div>
                </li>
                <li>Your vial holds about <strong>{first.res.dosesPerVial}</strong> doses at the starting dose. When it runs low, mix a new vial the same way.</li>
              </ol>

              {warnings.length > 0 && (
                <div className="guide-warnings">
                  {warnings.map((w, i) => <div className="warn" key={i}>⚠️ {w}</div>)}
                </div>
              )}

              <div className="guide-syringe">
                <SyringeVisual units={first.res.units} capacity={first.res.capacity} />
                <p className="muted small center mb-0" style={firstOverCap ? { color: 'var(--red)' } : undefined}>
                  {firstOverCap
                    ? `This dose needs ${first.res.units} units — it won’t fit a ${cap}-unit syringe.`
                    : `Starting dose: draw to ${first.res.units} units`}
                </p>
              </div>

              <div className="guide-actions">
                {user ? (
                  <button className="btn btn-primary btn-block" onClick={scheduleAll} disabled={busy}>
                    {busy ? 'Adding…' : '🗓️ Add this schedule to my calendar'}
                  </button>
                ) : (
                  <Link to="/register" className="btn btn-soft btn-block">Create a free account to save this to a calendar</Link>
                )}
                <Link to="/calculator" className="btn btn-ghost btn-block" style={{ marginTop: 8 }}>Open the full calculator</Link>
              </div>
            </div>
          </div>
        ) : (
          <div className="card empty" style={{ marginTop: 20 }}>
            <div className="emoji">✍️</div>
            <p style={{ margin: 0 }}>Fill in your vial, water, and dose above to get your step-by-step instructions.</p>
          </div>
        )}

        <div className="pep-disclaimer" style={{ marginTop: 22 }}>
          ⚠️ This tool only does the math on the values <strong>you</strong> enter — it is not medical advice and
          does not recommend any dose, medication, or schedule. Follow the exact dosing your prescriber gave you,
          and talk to a qualified healthcare professional with any questions.
        </div>
      </div>
    </main>
  )
}
